// Mobile / touch controls. Wires three input sources into the existing
// InputSystem:
//   1. Virtual aim joystick (left half of screen) → drives input.mouseX/Y so
//      the existing mouse-aim code in sample() handles pitch/yaw exactly the
//      same as on desktop.
//   2. Throttle slider (right edge, vertical) → drag-to-set throttle 0..1.
//   3. On-screen action buttons → fire / skills / boost.
//
// Gyroscope is a separate optional path (GyroControls.ts).

import type { InputSystem } from "./Input";

export interface MobileCallbacks {
  onSkill: (n: 1 | 2 | 3) => void;
  onBoost: () => void;
}

export class MobileControls {
  private root: HTMLElement;
  private aimZone: HTMLElement;
  private aimBase: HTMLElement;
  private aimKnob: HTMLElement;
  private throttleSlider: HTMLElement;
  private throttleFill: HTMLElement;
  private throttleReadout: HTMLElement;
  private fireBtn: HTMLElement;
  private boostBtn: HTMLElement;
  private skillBtns: HTMLElement[];

  private aimTouchId: number | null = null;
  private aimStartX = 0;
  private aimStartY = 0;
  private knobRadius = 60; // visual knob travel

  private throttleTouchId: number | null = null;

  constructor(private input: InputSystem, private cb: MobileCallbacks) {
    this.root = document.getElementById("touchControls")!;
    this.aimZone = document.getElementById("aimZone")!;
    this.aimBase = document.getElementById("aimStickBase")!;
    this.aimKnob = document.getElementById("aimStickKnob")!;
    this.throttleSlider = document.getElementById("throttleSlider")!;
    this.throttleFill = document.getElementById("throttleFill")!;
    this.throttleReadout = document.getElementById("throttleReadout")!;
    this.fireBtn = document.getElementById("touchFireBtn")!;
    this.boostBtn = document.getElementById("touchBoostBtn")!;
    this.skillBtns = Array.from(document.querySelectorAll<HTMLElement>(".touch-btn-skill"));

    this.bindAimZone();
    this.bindThrottle();
    this.bindFire();
    this.bindSkills();
    this.bindBoost();
    this.refreshThrottleVisual();
  }

  enable() { this.root.classList.remove("hidden"); }
  disable() { this.root.classList.add("hidden"); }
  isEnabled() { return !this.root.classList.contains("hidden"); }

  // ---- Aim joystick ----
  private bindAimZone() {
    const onStart = (e: TouchEvent) => {
      e.preventDefault();
      if (this.aimTouchId !== null) return;
      const t = e.changedTouches[0];
      this.aimTouchId = t.identifier;
      this.aimStartX = t.clientX;
      this.aimStartY = t.clientY;
      this.aimBase.style.left = t.clientX + "px";
      this.aimBase.style.top  = t.clientY + "px";
      this.aimBase.classList.remove("hidden");
      this.aimKnob.style.transform = "translate(-50%, -50%)";
    };
    const onMove = (e: TouchEvent) => {
      if (this.aimTouchId === null) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== this.aimTouchId) continue;
        e.preventDefault();
        let dx = t.clientX - this.aimStartX;
        let dy = t.clientY - this.aimStartY;
        const len = Math.hypot(dx, dy);
        const cap = this.knobRadius;
        if (len > cap) { dx = (dx / len) * cap; dy = (dy / len) * cap; }
        this.aimKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        // Map to virtual mouse coords. The mouse-aim code in InputSystem
        // expects mouseX/Y on screen and converts to ±1 axes by dividing
        // by half-min-dimension. We synthesize a position equivalent to a
        // joystick deflection scaled to the same range.
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const half = Math.min(cx, cy);
        const jx = dx / cap; // -1..1
        const jy = dy / cap;
        this.input.mouseX = cx + jx * half;
        this.input.mouseY = cy + jy * half;
      }
    };
    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== this.aimTouchId) continue;
        e.preventDefault();
        this.aimTouchId = null;
        this.aimBase.classList.add("hidden");
        // Recenter virtual cursor → no input.
        this.input.mouseX = window.innerWidth / 2;
        this.input.mouseY = window.innerHeight / 2;
      }
    };
    this.aimZone.addEventListener("touchstart", onStart, { passive: false });
    this.aimZone.addEventListener("touchmove", onMove, { passive: false });
    this.aimZone.addEventListener("touchend", onEnd, { passive: false });
    this.aimZone.addEventListener("touchcancel", onEnd, { passive: false });
  }

  // ---- Throttle slider (drag vertical to set, init at current input throttle) ----
  private bindThrottle() {
    const update = (clientY: number) => {
      const rect = this.throttleSlider.getBoundingClientRect();
      const t = 1 - (clientY - rect.top) / rect.height;
      this.input.setThrottle(Math.max(0, Math.min(1, t)));
      this.refreshThrottleVisual();
    };
    const onStart = (e: TouchEvent) => {
      if (this.throttleTouchId !== null) return;
      const t = e.changedTouches[0];
      this.throttleTouchId = t.identifier;
      e.preventDefault();
      update(t.clientY);
    };
    const onMove = (e: TouchEvent) => {
      if (this.throttleTouchId === null) return;
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== this.throttleTouchId) continue;
        e.preventDefault();
        update(t.clientY);
      }
    };
    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== this.throttleTouchId) continue;
        this.throttleTouchId = null;
      }
    };
    this.throttleSlider.addEventListener("touchstart", onStart, { passive: false });
    this.throttleSlider.addEventListener("touchmove", onMove, { passive: false });
    this.throttleSlider.addEventListener("touchend", onEnd, { passive: false });
    this.throttleSlider.addEventListener("touchcancel", onEnd, { passive: false });
  }

  refreshThrottleVisual() {
    const t = this.input.getThrottle();
    this.throttleFill.style.height = (t * 100).toFixed(0) + "%";
    this.throttleReadout.textContent = Math.round(t * 100).toString();
  }

  // ---- Fire button: hold-to-fire ----
  private bindFire() {
    const set = (down: boolean) => {
      if (down) this.input.pressed.add("Mouse0");
      else      this.input.pressed.delete("Mouse0");
      this.fireBtn.classList.toggle("is-pressed", down);
    };
    this.fireBtn.addEventListener("touchstart", (e) => { e.preventDefault(); set(true); }, { passive: false });
    this.fireBtn.addEventListener("touchend",   (e) => { e.preventDefault(); set(false); }, { passive: false });
    this.fireBtn.addEventListener("touchcancel",(e) => { e.preventDefault(); set(false); }, { passive: false });
  }

  // ---- Skill buttons (X/C/V) ----
  private bindSkills() {
    for (const btn of this.skillBtns) {
      const skill = Number(btn.dataset.skill) as 1 | 2 | 3;
      btn.addEventListener("touchstart", (e) => {
        e.preventDefault();
        btn.classList.add("is-pressed");
        this.cb.onSkill(skill);
      }, { passive: false });
      const release = () => btn.classList.remove("is-pressed");
      btn.addEventListener("touchend",   (e) => { e.preventDefault(); release(); }, { passive: false });
      btn.addEventListener("touchcancel",(e) => { e.preventDefault(); release(); }, { passive: false });
    }
  }

  private bindBoost() {
    this.boostBtn.addEventListener("touchstart", (e) => {
      e.preventDefault();
      this.boostBtn.classList.add("is-pressed");
      this.cb.onBoost();
    }, { passive: false });
    const release = () => this.boostBtn.classList.remove("is-pressed");
    this.boostBtn.addEventListener("touchend",   (e) => { e.preventDefault(); release(); }, { passive: false });
    this.boostBtn.addEventListener("touchcancel",(e) => { e.preventDefault(); release(); }, { passive: false });
  }

  static isMobile(): boolean {
    if (typeof window === "undefined") return false;
    if (window.matchMedia?.("(pointer: coarse)").matches) return true;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
}
